import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { EmptyState, Pill } from '../../../components';
import { cx } from '../../../lib/cx';
import {
  addHoliday,
  createCalendar,
  deleteCalendar,
  deleteHoliday,
  editHoliday,
  generateYear,
  holidayCalendarsQueryOptions,
  holidaysQueryOptions,
  type HolidayRow,
} from '../../calendar/api';
import { CONTROL_CLASS, InlineStatus, SectionCard } from '../fields';

/**
 * Which days the team is off, so a sprint can say so before its scope is
 * locked rather than after.
 *
 * Holidays are GENERATED once from an offline calendar and then stored as
 * ordinary editable rows, rather than computed on the fly. That is the whole
 * design: Pakistan's Eid dates are set by the moon sighting and the announced
 * day can land up to two days from the computed one, so the calendar's answer
 * has to be a starting point somebody can correct. Re-generating a year leaves
 * every existing row alone, so a correction survives.
 */
export function CalendarsSection({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient();
  const calendars = useQuery(holidayCalendarsQueryOptions(workspaceId));
  const holidays = useQuery(holidaysQueryOptions(workspaceId));
  const [country, setCountry] = useState('');
  const [subdivision, setSubdivision] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['ws', workspaceId, 'calendars'] }),
      queryClient.invalidateQueries({ queryKey: ['ws', workspaceId, 'holidays'] }),
    ]);
  };

  const add = useMutation({
    mutationFn: () =>
      createCalendar(workspaceId, {
        country: country.trim().toUpperCase(),
        subdivision: subdivision.trim() === '' ? null : subdivision.trim().toUpperCase(),
      }),
    onSuccess: () => {
      setCountry('');
      setSubdivision('');
    },
    onSettled: invalidate,
  });

  const generate = useMutation({
    mutationFn: (calendarId: string) => generateYear(workspaceId, calendarId, year),
    onSettled: invalidate,
  });

  const remove = useMutation({
    mutationFn: (calendarId: string) => deleteCalendar(workspaceId, calendarId),
    onSettled: invalidate,
  });

  const byCalendar = useMemo(() => {
    const map = new Map<string, HolidayRow[]>();
    for (const holiday of holidays.data ?? []) {
      const key = holiday.calendarId ?? '';
      const list = map.get(key) ?? [];
      list.push(holiday);
      map.set(key, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.onDate.localeCompare(b.onDate));
    return map;
  }, [holidays.data]);

  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        title="Add a country"
        description="Two-letter country code, and a state or province where it matters — AU holidays differ by state."
      >
        <form
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (country.trim() !== '') add.mutate();
          }}
          className="flex flex-wrap items-end gap-2.5"
        >
          <label className="flex flex-col gap-1">
            <span className="fs-11 text-muted">Country</span>
            <input
              value={country}
              onChange={(event) => setCountry(event.target.value)}
              placeholder="AU"
              maxLength={2}
              className={cx(CONTROL_CLASS, 'w-20 uppercase')}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="fs-11 text-muted">State (optional)</span>
            <input
              value={subdivision}
              onChange={(event) => setSubdivision(event.target.value)}
              placeholder="NSW"
              maxLength={6}
              className={cx(CONTROL_CLASS, 'w-28 uppercase')}
            />
          </label>
          <button
            type="submit"
            disabled={add.isPending || country.trim() === ''}
            className="h-8 rounded-md border border-accent bg-accent px-3 fs-13 font-medium text-white hover:bg-accent-strong disabled:cursor-not-allowed disabled:opacity-50"
          >
            {add.isPending ? 'Adding…' : 'Add calendar'}
          </button>
          <InlineStatus pending={false} error={add.error?.message ?? null} />
        </form>
      </SectionCard>

      {(calendars.data ?? []).length === 0 ? (
        <EmptyState
          title="No calendars yet"
          description="Add one above, then generate a year. Until then sprints assume every weekday is a working day."
        />
      ) : (
        (calendars.data ?? []).map((calendar) => (
          <SectionCard
            key={calendar.id}
            title={calendar.label}
            description={`${byCalendar.get(calendar.id)?.length ?? 0} days stored`}
            actions={
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={year}
                  onChange={(event) => setYear(Number(event.target.value))}
                  className={cx(CONTROL_CLASS, 'w-24')}
                  aria-label="Year to generate"
                />
                <button
                  type="button"
                  onClick={() => generate.mutate(calendar.id)}
                  disabled={generate.isPending}
                  className="h-8 rounded-md border border-line bg-base px-3 fs-12 font-semibold text-ink hover:bg-surface-2 disabled:opacity-50"
                >
                  {generate.isPending ? 'Generating…' : 'Generate'}
                </button>
                <button
                  type="button"
                  onClick={() => remove.mutate(calendar.id)}
                  aria-label={`Remove ${calendar.label}`}
                  className="rounded-md p-1.5 text-muted hover:text-danger"
                >
                  <Trash2 aria-hidden size={15} />
                </button>
              </div>
            }
          >
            <HolidayList
              workspaceId={workspaceId}
              holidays={byCalendar.get(calendar.id) ?? []}
              onChanged={invalidate}
            />
          </SectionCard>
        ))
      )}

      <SectionCard
        title="Company days"
        description="Days this team is off that no public calendar knows about — an office closure, a shutdown week."
      >
        <CompanyDayForm workspaceId={workspaceId} onChanged={invalidate} />
        <div className="mt-3">
          <HolidayList workspaceId={workspaceId} holidays={byCalendar.get('') ?? []} onChanged={invalidate} />
        </div>
      </SectionCard>
    </div>
  );
}

function CompanyDayForm({ workspaceId, onChanged }: { workspaceId: string; onChanged: () => Promise<void> }) {
  const [onDate, setOnDate] = useState('');
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => addHoliday(workspaceId, { onDate, name: name.trim() }),
    onSuccess: () => {
      setOnDate('');
      setName('');
    },
    onSettled: onChanged,
  });

  return (
    <form
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        if (onDate !== '' && name.trim() !== '') create.mutate();
      }}
      className="flex flex-wrap items-end gap-2.5"
    >
      <label className="flex flex-col gap-1">
        <span className="fs-11 text-muted">Date</span>
        <input
          type="date"
          value={onDate}
          onChange={(event) => setOnDate(event.target.value)}
          className={CONTROL_CLASS}
        />
      </label>
      <label className="flex min-w-0 flex-1 basis-48 flex-col gap-1">
        <span className="fs-11 text-muted">What it is</span>
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Office closed"
          className={CONTROL_CLASS}
        />
      </label>
      <button
        type="submit"
        disabled={create.isPending || onDate === '' || name.trim() === ''}
        className="h-8 rounded-md border border-line bg-base px-3 fs-12 font-semibold text-ink hover:bg-surface-2 disabled:opacity-50"
      >
        {create.isPending ? 'Adding…' : 'Add day'}
      </button>
      <InlineStatus pending={false} error={create.error?.message ?? null} />
    </form>
  );
}

function HolidayList({
  workspaceId,
  holidays,
  onChanged,
}: {
  workspaceId: string;
  holidays: readonly HolidayRow[];
  onChanged: () => Promise<void>;
}) {
  const edit = useMutation({
    mutationFn: ({ id, ...patch }: { id: string; onDate?: string; observed?: boolean }) =>
      editHoliday(workspaceId, id, patch),
    onSettled: onChanged,
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteHoliday(workspaceId, id),
    onSettled: onChanged,
  });

  if (holidays.length === 0) {
    return <p className="fs-12 text-muted">Nothing stored yet.</p>;
  }

  return (
    <ul className="flex flex-col divide-y divide-line">
      {holidays.map((holiday) => (
        <li key={holiday.id} className="flex flex-wrap items-center gap-2 py-1.5">
          <input
            type="date"
            value={holiday.onDate}
            onChange={(event) => edit.mutate({ id: holiday.id, onDate: event.target.value })}
            className={cx(CONTROL_CLASS, 'w-36', !holiday.observed && 'opacity-50')}
            aria-label={`Date of ${holiday.name}`}
          />
          <span
            className={cx(
              'min-w-0 flex-1 fs-12.5',
              holiday.observed ? 'text-ink' : 'text-faint line-through',
            )}
          >
            {holiday.name}
          </span>
          {/* The one thing an admin has to act on. A lunar date is a
              computed guess until the sighting is announced, and this is the
              only row anyone should feel obliged to check. */}
          {holiday.estimated && (
            <Pill tone="warn" title="Set by the moon sighting — confirm the announced date">
              estimated
            </Pill>
          )}
          <label className="flex items-center gap-1.5 fs-11.5 text-muted">
            <input
              type="checkbox"
              checked={holiday.observed}
              onChange={(event) => edit.mutate({ id: holiday.id, observed: event.target.checked })}
              className="accent-ink"
            />
            Off
          </label>
          <button
            type="button"
            onClick={() => remove.mutate(holiday.id)}
            aria-label={`Remove ${holiday.name}`}
            className="rounded-md p-1 text-muted hover:text-danger"
          >
            <Trash2 aria-hidden size={14} />
          </button>
        </li>
      ))}
    </ul>
  );
}
